'use client';

import { useMemo } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Settings01Icon } from '@terra/ui-icons';
import { IconButton, Tooltip, Scroll } from '@terra/ui-design-system';
import { ProfileMenu } from './profile/ProfileMenu';
import {
  useSidebarState,
  useMainContentTabs,
  usePanelVisibility,
  useSidebarMinimized,
  type SidebarTab,
} from '../../store/layoutStore';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarSection } from './SidebarSection';
import { TargetSelector } from './target-selector/TargetSelector';
import type { MouseEvent } from 'react';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import {
  getSidebarSections,
  getSidebarTabMapping,
  type SidebarSectionItem,
} from './utils';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { useTranslations } from 'next-intl';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { useNetworkDataSidebarBadges } from '../../hooks/layout/useNetworkDataSidebarBadges';
import { useProxyStore } from '../../store/proxy-store';
import { useAppTargetId } from '../../store/projectStore';
import { useSourceCodeInfo } from '../../hooks/dashboard/useSourceCodeInfo';
import { isAppTarget } from '../../utils/target-scope.utils';

const sidebarNavTourId = (itemId: SidebarTab): string | undefined => {
  if (itemId === 'requests' || itemId === 'signals') {
    return itemId;
  }

  return undefined;
};

export function Sidebar() {
  const { selectedTab, setSidebarTab } = useSidebarState();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const isNavRestricted = !targetId;
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const { setSidePanelsVisible } = usePanelVisibility();
  const { isSidebarMinimized } = useSidebarMinimized();
  const { isFeatureFlagEnabled: isExternalNetworkEnabled } = useFeatureFlags(
    'EXTERNAL_NETWORK_ENABLED',
  );
  const appTargetId = useAppTargetId();
  const { data: sourceCodeInfo } = useSourceCodeInfo(appTargetId ?? '');
  const hasApplicationSourceCode =
    !isAppTarget(targetType) ||
    sourceCodeInfo === undefined ||
    sourceCodeInfo.isConnected;
  const isInterceptActive = useProxyStore(
    (state) =>
      state.settings.interceptEnabled ||
      state.settings.interceptResponsesEnabled,
  );
  const t = useTranslations('layout');
  const sidebarLabels = useMemo(
    () => ({
      sectionDataAndAssets: t('sectionDataAndAssets'),
      sectionTrafficAndMonitoring: t('sectionTrafficAndMonitoring'),
      sectionProxyTools: t('sectionProxyTools'),
      assets: t('assets'),
      code: t('code'),
      signals: t('signals'),
      executions: t('executions'),
      findings: t('findings'),
      reports: t('reports'),
      httpHistory: t('httpHistory'),
      webSocketSse: t('webSocketSse'),
      intercept: t('intercept'),
      matchReplace: t('matchReplace'),
      replay: t('replay'),
      comparer: t('comparer'),
      interact: t('interact'),
      applicationSettings: t('applicationSettings'),
      proxySettings: t('proxySettings'),
      networkGraph: t('networkGraph'),
      targetOverview: t('targetOverview'),
      home: t('home'),
    }),
    [t],
  );
  const sidebarTabMapping = getSidebarTabMapping({
    ...sidebarLabels,
    targetType,
  });
  const { signalsHasNew, findingsHasNew } = useNetworkDataSidebarBadges({
    surfaceId: targetId,
    targetType,
  });
  const sidebarSections = useMemo(() => {
    const sidebarSectionOptions = {
      ...sidebarLabels,
      isExternalNetworkEnabled,
      targetType,
      hasApplicationSourceCode,
    };
    const sections = getSidebarSections(sidebarSectionOptions);
    const dataSection = sections[0];

    if (!dataSection) {
      return sections;
    }

    const dataItems = dataSection.items.map((item) => {
      if (item.id === 'signals') {
        return { ...item, hasNew: signalsHasNew };
      }

      if (item.id === 'findings') {
        return { ...item, hasNew: findingsHasNew };
      }

      return item;
    });

    return [{ ...dataSection, items: dataItems }, ...sections.slice(1)];
  }, [
    sidebarLabels,
    isExternalNetworkEnabled,
    targetType,
    hasApplicationSourceCode,
    signalsHasNew,
    findingsHasNew,
  ]);
  const dataSection = sidebarSections.find((section) => section.id === 'data');
  const trafficSection = sidebarSections.find(
    (section) => section.id === 'traffic',
  );
  const proxySection = sidebarSections.find(
    (section) => section.id === 'proxy',
  );
  const handleItemClick = (itemId: SidebarTab) => {
    if (isNavRestricted) {
      return;
    }

    setSidebarTab(itemId);
    const tabConfig = sidebarTabMapping[itemId];

    if (itemId === 'network-graph') {
      navigateInActiveTab(tabConfig);
      setSidePanelsVisible(false);

      return;
    }

    navigateInActiveTab(tabConfig);
    setSidePanelsVisible(true);
  };

  const handleOpenContextMenu = ({
    event,
    itemId,
  }: {
    event: MouseEvent<HTMLElement>;
    itemId: SidebarTab;
  }) => {
    if (isNavRestricted) {
      return;
    }

    const tab = sidebarTabMapping[itemId];

    openContextMenu({
      event,
      items: [{ type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab }],
    });
  };

  const renderNavItem = (item: SidebarSectionItem) => (
    <SidebarNavItem
      key={item.id}
      icon={item.icon}
      label={item.label}
      isActive={selectedTab === item.id}
      isSubItem={item.isSubItem}
      hasNew={item.hasNew}
      showIndicatorDot={item.id === 'intercept' && isInterceptActive}
      collapsed={isSidebarMinimized}
      disabled={isNavRestricted}
      tourId={sidebarNavTourId(item.id)}
      onClick={() => handleItemClick(item.id)}
      onContextMenu={(event) =>
        handleOpenContextMenu({ itemId: item.id, event })
      }
    />
  );
  const isProxySettingsActive = selectedTab === 'proxy-settings';
  const proxySettingsGear = (
    <Tooltip
      title={sidebarLabels.proxySettings}
      positioning={{ placement: 'top' }}
    >
      <Box display="inline-flex">
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={sidebarLabels.proxySettings}
          data-tour-id="proxy-settings"
          disabled={isNavRestricted}
          color={isProxySettingsActive ? 'fg.neutral.default' : undefined}
          bg={isProxySettingsActive ? 'bg.neutral.muted' : undefined}
          onClick={() => handleItemClick('proxy-settings')}
          onContextMenu={(event) =>
            handleOpenContextMenu({ itemId: 'proxy-settings', event })
          }
        >
          <Settings01Icon size={14} strokeWidth={1.75} />
        </IconButton>
      </Box>
    </Tooltip>
  );

  return (
    <Box
      h="full"
      w="full"
      pt="2"
      pb="0"
      bg="bg.light"
      borderRightWidth="1px"
      borderColor="border.neutral.subtle"
      display="flex"
      flexDirection="column"
      data-bi-unmask="true"
    >
      <Box
        px="2"
        pt="1"
        pb="2"
        flexShrink={0}
        display="flex"
        justifyContent={isSidebarMinimized ? 'center' : 'stretch'}
      >
        <TargetSelector collapsed={isSidebarMinimized} />
      </Box>

      <Box flex="1" minH="0" w="full">
        <Scroll orientation="vertical" showScrollbar h="full">
          <Box px={isSidebarMinimized ? '1' : '2'} pb="2">
            {dataSection && (
              <VStack gap="0" w="full" pt="4" alignItems="stretch">
                {dataSection.items.map(renderNavItem)}
                <SidebarNavItem
                  icon={Settings01Icon}
                  label={sidebarLabels.applicationSettings}
                  isActive={selectedTab === 'settings'}
                  collapsed={isSidebarMinimized}
                  disabled={isNavRestricted}
                  onClick={() => handleItemClick('settings')}
                  onContextMenu={(event) =>
                    handleOpenContextMenu({ itemId: 'settings', event })
                  }
                />
              </VStack>
            )}

            {trafficSection && trafficSection.items.length > 0 && (
              <SidebarSection
                label={trafficSection.sectionLabel}
                collapsed={isSidebarMinimized}
                hasActiveChild={trafficSection.items.some(
                  (item) => item.id === selectedTab,
                )}
              >
                {trafficSection.items.map(renderNavItem)}
              </SidebarSection>
            )}

            {proxySection && proxySection.items.length > 0 && (
              <SidebarSection
                label={proxySection.sectionLabel}
                collapsed={isSidebarMinimized}
                hasActiveChild={
                  isProxySettingsActive ||
                  proxySection.items.some((item) => item.id === selectedTab)
                }
                headerAction={proxySettingsGear}
                keepHeaderActionVisible={isProxySettingsActive}
              >
                {proxySection.items.map(renderNavItem)}
              </SidebarSection>
            )}
          </Box>
        </Scroll>
      </Box>

      <Box
        flexShrink={0}
        w="full"
        borderTopWidth="1px"
        borderTopStyle="solid"
        borderTopColor="border.neutral.subtle"
        display="flex"
        alignItems="center"
        justifyContent={isSidebarMinimized ? 'center' : 'stretch'}
        {...(isSidebarMinimized
          ? {
              px: '1',
              h: '12',
            }
          : {
              p: '2',
            })}
      >
        <ProfileMenu collapsed={isSidebarMinimized} />
      </Box>
    </Box>
  );
}
